import React from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { DocumentTable } from '../components/DocumentTable';
import { Button } from '@/components/ui/button';
import { Link } from '@tanstack/react-router';
import { Plus, Download, RefreshCcw, Database } from 'lucide-react';
import { DocumentsService, PaginatedDocuments } from '../services/documents-service';
import { Document } from '../types';
import { useToast } from '@/hooks/use-toast';
import { documentCacheService } from '@/services/document-cache-service';

export function DocumentsPage() {
  const [paginatedData, setPaginatedData] = React.useState<PaginatedDocuments>({
    documents: [],
    total: 0,
    currentPage: 1,
    totalPages: 0,
    pageSize: 20,
    fromCache: false
  });
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isWarmingCache, setIsWarmingCache] = React.useState(false);
  const { toast } = useToast();

  // Initialize cache warming on first load
  React.useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Start cache warming in background
        const cacheStats = documentCacheService.getStats();
        
        if (cacheStats.totalDocuments === 0) {
          console.log('🔥 Cache is empty, starting cache warming...');
          setIsWarmingCache(true);
          
          // Show warming toast
          toast({
            title: 'Cache Warming',
            description: 'Loading all documents for optimal performance...',
          });
          
          // Start cache warming in background
          DocumentsService.warmCache().then(() => {
            console.log('🎉 Cache warming completed!');
            setIsWarmingCache(false);
            toast({
              title: 'Cache Ready',
              description: 'All documents loaded! Navigation will now be instant.',
            });
          }).catch((error) => {
            console.error('❌ Cache warming failed:', error);
            setIsWarmingCache(false);
            toast({
              title: 'Cache Warning',
              description: 'Cache warming failed, but documents will still load normally.',
              variant: 'destructive',
            });
          });
        }
        
        // Load first page immediately
        const firstPage = await DocumentsService.getDocumentsPage(1, 20);
        setPaginatedData(firstPage);
        
      } catch (err: any) {
        console.error('Failed to initialize documents:', err);
        setError(err.message || 'Failed to load documents.');
        toast({
          title: 'Error',
          description: err.message || 'Failed to load documents. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, []);

  const handleDocumentUpdate = (updatedDocument: Document) => {
    setPaginatedData(prev => ({
      ...prev,
      documents: prev.documents.map(doc => 
        doc.id === updatedDocument.id ? updatedDocument : doc
      )
    }));
  };

  const handlePageChange = async (page: number) => {
    try {
      setIsLoading(true);
      const newPageData = await DocumentsService.getDocumentsPage(page, paginatedData.pageSize);
      setPaginatedData(newPageData);
    } catch (error: any) {
      console.error('Failed to load page:', error);
      toast({
        title: 'Error',
        description: 'Failed to load page. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageSizeChange = async (pageSize: number) => {
    try {
      setIsLoading(true);
      const newPageData = await DocumentsService.getDocumentsPage(1, pageSize);
      setPaginatedData(newPageData);
    } catch (error: any) {
      console.error('Failed to change page size:', error);
      toast({
        title: 'Error',
        description: 'Failed to change page size. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFiltersChange = async (filters: any) => {
    try {
      setIsLoading(true);
      // Convert filters to DocumentFilter format
      const documentFilters = {
        search_term: filters.search_term,
        document_type: filters.document_type,
        visibility: filters.visibility
      };
      
      const newPageData = await DocumentsService.getDocumentsPage(1, paginatedData.pageSize, documentFilters);
      setPaginatedData(newPageData);
    } catch (error: any) {
      console.error('Failed to apply filters:', error);
      toast({
        title: 'Error',
        description: 'Failed to apply filters. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCacheRefresh = async () => {
    try {
      setIsWarmingCache(true);
      toast({
        title: 'Refreshing Cache',
        description: 'Updating document cache...',
      });
      
      // Clear cache and warm it again
      documentCacheService.clearCache();
      await DocumentsService.warmCache();
      
      // Reload current page
      const refreshedPage = await DocumentsService.getDocumentsPage(
        paginatedData.currentPage, 
        paginatedData.pageSize
      );
      setPaginatedData(refreshedPage);
      
      toast({
        title: 'Cache Refreshed',
        description: 'Document cache has been updated successfully.',
      });
    } catch (error: any) {
      console.error('Cache refresh failed:', error);
      toast({
        title: 'Refresh Failed',
        description: 'Failed to refresh cache. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsWarmingCache(false);
    }
  };

  const getCacheStatsDisplay = () => {
    // Hide cache stats if cache system is disabled (indicated by hitRate being 0 and no real cache data)
    if (!paginatedData.cacheStats || (paginatedData.cacheStats.hitRate === 0 && paginatedData.cacheStats.cacheHits === 0 && paginatedData.cacheStats.cacheMisses === 0)) {
      return null;
    }
    
    const stats = paginatedData.cacheStats;
    const hitRate = Math.round(stats.hitRate * 100);
    
    return (
      <div className="text-sm text-muted-foreground">
        📊 Cache: {stats.totalDocuments} docs | Hit rate: {hitRate}% | 
        {paginatedData.fromCache ? ' 📦 From cache' : ' 🌐 From API'}
      </div>
    );
  };

  return (
    <>
      <PageHeader
        title="Documents"
        description="Manage historical documents in the system."
        actions={
          <>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCacheRefresh}
              disabled={isWarmingCache}
            >
              <RefreshCcw className={`mr-2 h-4 w-4 ${isWarmingCache ? 'animate-spin' : ''}`} />
              {isWarmingCache ? 'Refreshing...' : 'Refresh Cache'}
            </Button>
            <Button variant="outline" size="sm">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
            <Button asChild size="sm">
              <Link to="/documents/add">
                <Plus className="mr-2 h-4 w-4" />
                Add Document
              </Link>
            </Button>
          </>
        }
      />
      
      <div className="space-y-4">
        
        {isLoading ? (
          <div className="text-center space-y-2">
            <p>Loading documents...</p>
            {isWarmingCache && (
              <p className="text-sm text-muted-foreground">
                🔥 Warming cache for optimal performance...
              </p>
            )}
          </div>
        ) : error ? (
          <div className="text-center space-y-4">
            <p className="text-red-500">Error: {error}</p>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        ) : (
          <DocumentTable 
            data={paginatedData.documents} 
            onDocumentUpdate={handleDocumentUpdate}
            paginationInfo={paginatedData}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onFiltersChange={handleFiltersChange}
            isLoading={isLoading}
          />
        )}

        {/* Cache Stats Display */}
        {getCacheStatsDisplay() && (
          <div className="bg-muted/50 p-3 rounded-lg">
            {getCacheStatsDisplay()}
          </div>
        )}

      </div>
    </>
  );
}

export default DocumentsPage;
